import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link } from "react-router-dom";
import { Mail as MailIcon } from "lucide-react";
import { AxiosError } from "axios";

import StarterContainer from "@/components/StarterContainer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FormError } from "@/components/FormError";
import { ErrorBox } from "@/components/ui/error-box";
import { useLoading } from "@/lib/LoadingHook";
import { useAppToast } from "@/hooks/useAppToast";
import { useAppNavigate } from "@/lib/AppNavigatorHook";
import { isBadRequest } from "@/lib/errorUtils";
import { requestPasswordReset } from "@/services/shared/auth/forgotPassword.service";

// Define the form data structure
interface ForgotPasswordFormInputs {
  email: string;
}

// Define the validation schema using yup
const schema = yup.object().shape({
  email: yup.string().email("Email inválido").required("Email é obrigatório"),
});

export const ForgotPasswordPage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormInputs>({
    resolver: yupResolver(schema),
  });

  const { isLoading, whenLoading } = useLoading();
  const { success, error: toastError } = useAppToast();
  const { goToLogin } = useAppNavigate();

  const [submissionError, setSubmissionError] = React.useState<string | null>(
    null
  );

  const onSubmit = async (data: ForgotPasswordFormInputs) => {
    setSubmissionError(null);
    try {
      await whenLoading(async () => {
        await requestPasswordReset(data.email);
        // Placeholder for actual API call
        // await requestPasswordReset(data.email);
        console.log("Requesting password reset for:", data.email);
        // Simulate API call success
        await new Promise((resolve) => setTimeout(resolve, 1000));
      });
      success("Se um email válido for fornecido, um link de redefinição de senha será enviado.");
      goToLogin();
    } catch (err) {
      if (err instanceof AxiosError) {
        if (isBadRequest(err)) {
          setSubmissionError(
            err.response?.data?.message || "Email não encontrado."
          );
        } else {
          setSubmissionError("Ocorreu um erro. Por favor, tente novamente.");
        }
      } else {
        setSubmissionError("Ocorreu um erro inesperado.");
      }
      toastError("Erro ao solicitar redefinição de senha.");
    }
  };

  return (
    <StarterContainer>
      <h2 className="text-2xl font-bold">Esqueceu sua senha?</h2>
      <p className="mb-4">
        Digite seu email abaixo e enviaremos um link para redefinir sua
        senha.
      </p>
      <ErrorBox message={submissionError} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="block font-medium">
            Email
          </Label>
          <div className="relative flex items-center">
            <MailIcon className="absolute left-3 text-muted-foreground h-5 w-5" />
            <Input
              id="email"
              type="email"
              placeholder="m@example.com"
              className="pl-10"
              {...register("email")}
            />
          </div>
          <FormError message={errors.email?.message} />
        </div>
        <Button
          type="submit"
          className="w-full bg-black text-white py-2 rounded-md"
          isLoading={isLoading}
        >
          Enviar link de redefinição
        </Button>
      </form>
      <div className="text-center text-sm mt-4">
        Lembrou da sua senha?{" "}
        <Link to="/login" className="text-primary font-bold hover:underline">
          Fazer login
        </Link>
      </div>
    </StarterContainer>
  );
};
